import { useMemo, useState } from 'react';
import { format } from 'date-fns';

import { useAppStore } from '@/core/store/app.store';
import { useGetDispatchPlaces, useGetDispatchPlacesMetrics } from '@/features/dispatch-places/dispatch-places.queries';
import { serialiseToCsv, downloadCsv } from '@/utils/csv';

import type { DispatchPlaceStatus, DispatchPlaceMetrics } from '@/features/dispatch-places/dispatch-places.types';

export type SortField = 'name' | 'capacity' | 'status' | 'city';
export type SortDirection = 'asc' | 'desc';
export type StatusFilter = DispatchPlaceStatus | 'all';

/**
 * Normalises a string for fuzzy search: lowercase + remove accents.
 */
function normalise(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

/**
 * Returns true if every word in the query appears somewhere in the target.
 * Splitting by whitespace gives basic multi-word fuzzy matching.
 */
function fuzzyMatch(target: string, query: string): boolean {
  const normalised_target = normalise(target);
  const words = normalise(query).trim().split(/\s+/);
  return words.every((word) => normalised_target.includes(word));
}

/**
 * Zero-filled fallback used for dispatch places that have no orders yet.
 */
const EMPTY_METRICS: Omit<DispatchPlaceMetrics, 'dispatch_place_id'> = {
  por_recibir: 0,
  en_punto: 0,
  recolectadas: 0,
  entregadas: 0,
  por_devolver: 0,
  canceladas: 0,
};

/**
 * Controller hook for the Dispatch Places list page.
 * Handles search, sort and status filter entirely on the client side
 * since the list fits in memory (all records fetched in one request).
 *
 * Also loads per-place order metrics and exposes a CSV download handler.
 */
export const useDispatchPlacesPage = () => {
  const country = useAppStore((state) => state.country);
  const { data, isLoading, isError, isFetching } = useGetDispatchPlaces();
  const {
    data: metrics_data,
    isFetching: is_metrics_fetching,
  } = useGetDispatchPlacesMetrics();

  const [search_query, setSearchQuery] = useState('');
  const [status_filter, setStatusFilter] = useState<StatusFilter>('all');
  const [sort_field, setSortField] = useState<SortField>('name');
  const [sort_direction, setSortDirection] = useState<SortDirection>('asc');

  const onSortChange = (field: SortField) => {
    if (field === sort_field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  /**
   * O(1) lookup table: dispatch_place_id → its metrics row.
   * Built once from the flat metrics list; rebuilt only when metrics_data changes.
   */
  const metrics_by_id = useMemo<Record<string, DispatchPlaceMetrics>>(() => {
    if (!metrics_data) return {};

    return Object.fromEntries(
      metrics_data.items.map((item) => [item.dispatch_place_id, item]),
    );
  }, [metrics_data]);

  const filtered_items = useMemo(() => {
    const all_items = data?.items ?? [];

    const after_status = status_filter === 'all'
      ? all_items
      : all_items.filter((place) => place.status === status_filter);

    const after_search = search_query.trim() === ''
      ? after_status
      : after_status.filter((place) => {
          const city = place.address?.city ?? '';
          const searchable = `${place.name} ${city} ${place.country_code}`;
          return fuzzyMatch(searchable, search_query);
        });

    return [...after_search].sort((a, b) => {
      let comparison = 0;

      if (sort_field === 'name') {
        comparison = a.name.localeCompare(b.name, 'es');
      } else if (sort_field === 'capacity') {
        comparison = (a.capacity ?? 0) - (b.capacity ?? 0);
      } else if (sort_field === 'status') {
        comparison = a.status.localeCompare(b.status, 'es');
      } else if (sort_field === 'city') {
        const city_a = a.address?.city ?? '';
        const city_b = b.address?.city ?? '';
        comparison = city_a.localeCompare(city_b, 'es');
      }

      return sort_direction === 'asc' ? comparison : -comparison;
    });
  }, [data, search_query, status_filter, sort_field, sort_direction]);

  /**
   * Builds and downloads a CSV of every dispatch place in the active country
   * (the full list, ignoring the current search / status filter) joined with
   * its order metrics.  Columns are in the order requested by the business.
   */
  const onDownloadCsv = () => {
    const all_items = data?.items ?? [];
    const today = format(new Date(), 'yyyy-MM-dd');
    const filename = `puntos99-${country.toLowerCase()}-${today}.csv`;

    const rows = all_items.map((place) => {
      const metrics = metrics_by_id[place.id] ?? EMPTY_METRICS;
      return {
        Nombre: place.name,
        Ciudad: place.address?.city ?? '',
        País: place.country_code.toUpperCase(),
        Tipo: place.type,
        Estado: place.status,
        Capacidad: place.capacity,
        'Por recibir': metrics.por_recibir,
        'En punto': metrics.en_punto,
        Recolectadas: metrics.recolectadas,
        Entregadas: metrics.entregadas,
        'Por devolver': metrics.por_devolver,
        Canceladas: metrics.canceladas,
      };
    });

    downloadCsv(serialiseToCsv(rows), filename);
  };

  return {
    items: filtered_items,
    total: data?.total ?? 0,
    displayed_count: filtered_items.length,
    isLoading,
    isFetching,
    is_metrics_fetching,
    isError,
    search_query,
    status_filter,
    sort_field,
    sort_direction,
    metrics_by_id,
    onSearchChange: setSearchQuery,
    onStatusFilterChange: setStatusFilter,
    onSortChange,
    onDownloadCsv,
  };
};
